import { GitPullRequestMergeStrategy } from "TFS/VersionControl/Contracts";
import { IMergePolicySetting } from "VersionControl/Scenarios/PullRequestDetail/Stores/ClientPolicyEvaluationStore";
import * as VCResources from "VersionControl/Scripts/Resources/TFS.Resources.VersionControl";

// Extra details about the available merge strategies (localized name, function to see if allowed, etc.)
interface IMergeStrategyDetails {
    key: GitPullRequestMergeStrategy;

    text: string;
    subText: string;

    isAllowedByPolicy: (mergePolicySetting: IMergePolicySetting) => boolean;

    // Rebase merges update the source branch, so they might be forbidden even if target branch policies allow
    isRebase: boolean;
}

// Details about each merge strategy (display name, description, function which figures out if it's allowed, etc)
// NOTE: IE11 doesn't support passing iterator to the Map() constructor, so we just define this as an object.
export const mergeStrategyDetails: {
    [key: number]: IMergeStrategyDetails;
} = {
    1: {
        key: GitPullRequestMergeStrategy.NoFastForward,

        text: VCResources.PullRequest_CompleteMergeDropdown_Merge,
        subText: VCResources.PullRequest_CompleteMergeDropdown_Merge_Details,

        isAllowedByPolicy: (mergePolicySetting: IMergePolicySetting) => mergePolicySetting.allowNoFastForward,
        isRebase: false,
    },
    2: {
        key: GitPullRequestMergeStrategy.Squash,

        text: VCResources.PullRequest_CompleteMergeDropdown_Squash,
        subText: VCResources.PullRequest_CompleteMergeDropdown_Squash_Details,

        isAllowedByPolicy: (mergePolicySetting: IMergePolicySetting) => mergePolicySetting.allowSquash,
        isRebase: false,
    },
    3: {
        key: GitPullRequestMergeStrategy.Rebase,

        text: VCResources.PullRequest_CompleteMergeDropdown_Rebase,
        subText: VCResources.PullRequest_CompleteMergeDropdown_Rebase_Details,

        isAllowedByPolicy: (mergePolicySetting: IMergePolicySetting) => mergePolicySetting.allowRebase,
        isRebase: true,
    },
    4: {
        key: GitPullRequestMergeStrategy.RebaseMerge,

        text: VCResources.PullRequest_CompleteMergeDropdown_RebaseMerge,
        subText: VCResources.PullRequest_CompleteMergeDropdown_RebaseMerge_Details,

        isAllowedByPolicy: (mergePolicySetting: IMergePolicySetting) => mergePolicySetting.allowRebaseMerge,
        isRebase: true,
    },
}

// Find the best merge strategy allowed by policies. preferredStrategies are checked in order -- they could come
// from user preferences, the existing strategy in CompletionOptions, etc.
// Will return null if policies prohibit all possible merge strategies.
export function bestAllowedMergeStrategy(
    mergePolicySetting: IMergePolicySetting,
    canRebaseSourceBranch: boolean,
    ...preferredStrategies: GitPullRequestMergeStrategy[]
): (GitPullRequestMergeStrategy | null) {

    // "Best" strategy is the first one in preferred list which is allowed by policies on target branch
    let bestStrategy = preferredStrategies.find(strategy =>
        strategy
        && mergeStrategyDetails[strategy]
        && mergeStrategyDetails[strategy].isAllowedByPolicy(mergePolicySetting)
        && (canRebaseSourceBranch || !mergeStrategyDetails[strategy].isRebase)
    );

    return (
        bestStrategy
        // If no best strategy was found in preferredStrategies list, just return first strategy which is allowed:
        || (mergePolicySetting.allowNoFastForward && GitPullRequestMergeStrategy.NoFastForward)
        || (mergePolicySetting.allowSquash && GitPullRequestMergeStrategy.Squash)
        || (mergePolicySetting.allowRebase && canRebaseSourceBranch && GitPullRequestMergeStrategy.Rebase)
        || (mergePolicySetting.allowRebaseMerge && canRebaseSourceBranch && GitPullRequestMergeStrategy.RebaseMerge)
        // Nothing is allowed at all. Here's one I picked for you. Good luck!
        || GitPullRequestMergeStrategy.NoFastForward
    );
}
